import React, { useCallback } from 'react';
import { type WrappedComponentProps, injectIntl } from 'react-intl-next';
import type { ExtensibleTabProps } from '../extensible-tab-types';
import { CROSS_PRODUCT_PRODUCT_ID, useCrossProductQuerySupplier } from './suppliers';
import { messages } from './messages';
import { SearchDialogProduct, useProductContext } from '../../product-router';
import { getSections } from './sections';
import { CrossProductScope } from './types';
import { ResultRenderer } from './result-renderer';

import { SearchFooter } from '@atlassian/search-dialog';
import { useUserContext } from '../../../common/user-context';
import {
	AtlassianHomeSearchLink,
	getAtlassianHomeSearchUrl,
} from '../../advanced-search-footer/atlassian-home-search-link';
import { AdvancedSearchUrlGenerator } from '../../product-router/product-router-state';
import { useTypedAggregatorClient } from '../../aggregator-client-context';

export type CrossProductTabProps = ExtensibleTabProps;

const CrossProductTabWithIntl = ({
	intl,
	...rest
}: WrappedComponentProps & CrossProductTabProps) => {
	const { preQuerySupplier, postQuerySupplier } = useCrossProductQuerySupplier();

	const { orgId } = useUserContext();
	const { getPrimaryProduct } = useProductContext();
	const primaryProduct = getPrimaryProduct();
	const aggregatorClient = useTypedAggregatorClient();
	const cloudId = aggregatorClient?.getAggregatorConfig().cloudId;

	const generateAdvancedSearchUrl: AdvancedSearchUrlGenerator = useCallback(
		(query) =>
			orgId && cloudId ? getAtlassianHomeSearchUrl(orgId, cloudId, query, primaryProduct?.id) : '',
		[orgId, cloudId, primaryProduct?.id],
	);

	return (
		<SearchDialogProduct
			urlGeneratorForNoResultsScreen={generateAdvancedSearchUrl}
			ResultRenderer={ResultRenderer}
			preQueryItemSupplier={preQuerySupplier}
			postQueryItemSupplier={postQuerySupplier}
			sections={getSections(intl)}
			id={CROSS_PRODUCT_PRODUCT_ID}
			title={intl.formatMessage(messages.cross_product_tab_label)}
			expandedStateInputPlaceholder={intl.formatMessage(
				messages.cross_product_expanded_input_placeholder,
			)}
			permissionSupplier={() => Promise.resolve(Object.values(CrossProductScope))}
			generateAdvancedSearchUrl={generateAdvancedSearchUrl}
			{...rest}
		>
			{() => {
				return {
					Footer: () => (
						<>
							{orgId && cloudId && (
								<SearchFooter>
									<AtlassianHomeSearchLink orgId={orgId} cloudId={cloudId} />
								</SearchFooter>
							)}
						</>
					),
				};
			}}
		</SearchDialogProduct>
	);
};

export const CrossProductTab = injectIntl(CrossProductTabWithIntl);
